import { Breadcrumbs } from '@design-system/breadcrumbs';
import { Container } from '@design-system/container';
import { ExtendableImage } from '@design-system/extendable-image';
import { HeadingBlock } from '@design-system/heading-block';
import { Section } from '@design-system/section';
import { ShareBlock } from '@design-system/share-block';
import classNames from 'classnames';
import React, { forwardRef } from 'react';

import CategoryList from '../HCP/CategoryList/CategoryList';
import PresentersList from '../HCP/PresenterList/PresenterList';
import { PageHeaderProps } from './models';

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  // eslint-disable-next-line complexity
  (
    {
      className,
      mode,
      imageElement,
      headingBlock,
      hiddenPageHeading,
      disabledLayoutBackground,
      variant,
      breadcrumbs,
      showBreadcrumbs = true,
      presenters,
      showShareBlock = false,
      shareBlock,
      categories = [],
      ...htmlProperties
    },
    ref
  ) => {
    const categoryList = categories.filter(
      ({ use_in_lables }) => use_in_lables
    );

    return (
      <Section
        ref={ref}
        className={classNames(
          'cds-page-header',
          {
            [`cds-page-header--${variant}`]: variant,
            'cds-page-header--no-media': !imageElement,
            'cds-page-header--no-headline': !headingBlock?.headline,
          },
          className
        )}
        mode="transparent"
        removeAllSpacing
        element="header"
        {...htmlProperties}
      >
        <Section mode={mode} className="cds-page-header__background">
          <Container
            className="cds-page-header__container"
            maxWidth={variant === 'standard' ? 'max-grid' : 'max-text'}
          >
            <div className="cds-page-header__text">
              {hiddenPageHeading ? (
                <h1 className="cds-sr-only">{hiddenPageHeading}</h1>
              ) : null}
              {showBreadcrumbs && breadcrumbs ? (
                <Breadcrumbs {...breadcrumbs} />
              ) : null}
              {categoryList && <CategoryList categoryList={categoryList} />}
              {headingBlock && (
                <HeadingBlock
                  className="cds-page-header__heading"
                  align={variant === 'standard' ? 'start' : 'center'}
                  {...headingBlock}
                  headlineTag={hiddenPageHeading ? 'h2' : 'h1'}
                />
              )}
              {presenters && <PresentersList presenters={presenters} />}
              {showShareBlock && shareBlock ? (
                <ShareBlock
                  align={variant === 'standard' ? 'start' : 'center'}
                  {...shareBlock}
                />
              ) : null}
            </div>
          </Container>
        </Section>
        {imageElement && (
          <div
            className="cds-page-header__image"
            aria-hidden={(presenters?.length ?? 0) > 0 || undefined}
          >
            <ExtendableImage
              className="cds-page-header__image-img"
              {...imageElement}
            />
          </div>
        )}
      </Section>
    );
  }
);

PageHeader.displayName = 'PageHeader';
